import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, formatImageUrl } from '../../utils/security';
import { Plus, Search, Edit3, Trash2, CheckCircle2, AlertTriangle, Layers } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminBlogList = () => {
    const { user, isBlogManager, isSuperAdmin, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grouped');

    useEffect(() => {
        if (!authLoading && !(isSuperAdmin() || isBlogManager())) navigate('/admin');
    }, [authLoading, isSuperAdmin, isBlogManager, navigate]);

    const fetchBlogs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchWithSecurity(`${API_URL}blogs.php?admin=1`);
            const data = await response.json();
            if (Array.isArray(data)) setBlogs(data);
        } catch (error) {
            console.error('Error fetching blogs:', error);
        }
        setLoading(false);
    }, []);

    useEffect(() => {
        if (user) fetchBlogs();
    }, [user, fetchBlogs]);

    const deleteBlog = async (id) => {
        if (!window.confirm(t('admin.blogList.deleteConfirm') || 'დარწმუნებული ხართ, რომ გსურთ ამ პოსტის წაშლა?')) return;
        try {
            const response = await fetchWithSecurity(`${API_URL}blogs.php?id=${id}`, { method: 'DELETE' });
            const result = await response.json();
            if (result.success) {
                setBlogs(prev => prev.filter(b => b.id !== id));
            } else {
                alert('შეცდომა: ' + (result.error || 'წაშლა ვერ მოხერხდა'));
            }
        } catch (error) {
            console.error('Error deleting blog:', error);
        }
    };

    const groupedBySlug = blogs.reduce((acc, item) => {
        const key = item.slug || item.id;
        if (!acc[key]) acc[key] = {};
        acc[key][item.lang || 'en'] = item;
        return acc;
    }, {});

    const filteredBlogs = blogs.filter(b => 
        (b.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (b.slug || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (b.author || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredGroups = Object.entries(groupedBySlug).filter(([slug, versions]) =>
        (versions.en?.title || versions.ka?.title || slug).toLowerCase().includes(searchTerm.toLowerCase()) ||
        slug.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (authLoading || !user) return null;

    const LangBadge = ({ lang }) => {
        if (lang === 'ka') return <span className="shadcn-badge shadcn-badge-warning">🇬🇪 KA</span>;
        return <span className="shadcn-badge shadcn-badge-info">🇬🇧 EN</span>;
    };

    return (
        <AdminLayout title={t('admin.blogList.title') || 'ბლოგისა და სიახლეების მართვა'}>
            <div className="shadcn-card">
                {/* ── CARD TOOLBAR: VIEW MODES, SEARCH & ACTION BUTTON ── */}
                <div className="d-flex flex-column flex-lg-row gap-3 mb-4 align-items-lg-center justify-content-between">
                    {/* View Mode Pills */}
                    <div className="d-flex gap-2 flex-wrap">
                        <button 
                            className={`shadcn-btn shadcn-btn-sm ${viewMode === 'grouped' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => setViewMode('grouped')}
                        >
                            <Layers size={14} /> {t('admin.blogList.grouped') || 'დაჯგუფებული ხედვა'}
                        </button>
                        <button 
                            className={`shadcn-btn shadcn-btn-sm ${viewMode === 'en' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => setViewMode('en')}
                        >
                            🇬🇧 English
                        </button>
                        <button 
                            className={`shadcn-btn shadcn-btn-sm ${viewMode === 'ka' ? 'shadcn-btn-primary' : 'shadcn-btn-secondary'}`}
                            onClick={() => setViewMode('ka')}
                        >
                            🇬🇪 ქართული
                        </button>
                    </div>

                    {/* Search Bar & Add Button */}
                    <div className="d-flex align-items-center gap-3 ms-lg-auto flex-wrap flex-lg-nowrap">
                        <div className="header-search-bar" style={{ width: '260px' }}>
                            <Search size={16} className="text-muted" />
                            <input
                                type="text"
                                placeholder={t('admin.blogList.searchPlaceholder') || 'ძებნა სათაურით ან სლაგით...'}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button className="shadcn-btn shadcn-btn-primary flex-shrink-0" onClick={() => navigate('/admin/blogs/new')}>
                            <Plus size={16} />
                            <span>{t('admin.blogList.createNew') || 'ახალი პოსტის შექმნა'}</span>
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>{t('admin.blogList.loading') || 'სტატიები იტვირთება...'}</span>
                    </div>
                ) : viewMode === 'grouped' ? (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>{t('admin.blogList.colSlugTitle') || 'სათაური & სლაგი'}</th>
                                    <th>{t('admin.blogList.colAuthor') || 'ავტორი'}</th>
                                    <th className="text-center">{t('admin.blogList.colEnVer') || '🇬🇧 ინგლისური ვერსია'}</th>
                                    <th className="text-center">{t('admin.blogList.colKaVer') || '🇬🇪 ქართული ვერსია'}</th>
                                    <th>{t('admin.blogList.colStatus') || 'სტატუსი'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredGroups.map(([slug, versions]) => {
                                    const en = versions.en;
                                    const ka = versions.ka;
                                    const hasEn = !!en;
                                    const hasKa = !!ka;
                                    const isComplete = hasEn && hasKa;
                                    const preview = en || ka;
                                    return (
                                        <tr key={slug}>
                                            <td>
                                                <div className="d-flex align-items-center gap-3">
                                                    {preview?.image && (
                                                        <img
                                                            src={formatImageUrl(preview.image)}
                                                            alt=""
                                                            style={{ width: '48px', height: '36px', objectFit: 'cover', borderRadius: '6px' }}
                                                        />
                                                    )}
                                                    <div>
                                                        <div className="fw-bold text-dark">{preview?.title || slug}</div>
                                                        <div className="smallest text-muted">{slug}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="small text-muted">{preview?.author}</td>
                                            <td className="text-center">
                                                {hasEn ? (
                                                    <div>
                                                        <div className="smallest text-muted mb-1">{en.date}</div>
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" onClick={() => navigate(`/admin/blogs/edit/${en.id}`)}>
                                                                <Edit3 size={13} />
                                                            </button>
                                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteBlog(en.id)}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary text-success" onClick={() => navigate('/admin/blogs/new')}>
                                                        <Plus size={12} /> {t('admin.blogList.addEn') || 'დაამატე EN'}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                {hasKa ? (
                                                    <div>
                                                        <div className="smallest text-muted mb-1">{ka.date}</div>
                                                        <div className="d-flex gap-1 justify-content-center">
                                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" onClick={() => navigate(`/admin/blogs/edit/${ka.id}`)}>
                                                                <Edit3 size={13} />
                                                            </button>
                                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteBlog(ka.id)}>
                                                                <Trash2 size={13} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary text-warning" onClick={() => navigate('/admin/blogs/new')}>
                                                        <Plus size={12} /> {t('admin.blogList.addKa') || 'დაამატე KA'}
                                                    </button>
                                                )}
                                            </td>
                                            <td>
                                                {isComplete ? (
                                                    <span className="shadcn-badge shadcn-badge-success">
                                                        <CheckCircle2 size={13} /> {t('admin.blogList.badgeComplete') || 'სრული'}
                                                    </span>
                                                ) : (
                                                    <span className="shadcn-badge shadcn-badge-warning">
                                                        <AlertTriangle size={13} /> {t('admin.blogList.missingLang', { lang: !hasEn ? 'EN' : 'KA' }) || `აკლია ${!hasEn ? 'EN' : 'KA'}`}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {filteredGroups.length === 0 && <p className="text-center text-muted py-5 mb-0">{t('admin.blogList.noPosts') || 'სტატიები არ მოიძებნა.'}</p>}
                    </div>
                ) : (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>{t('admin.blogList.colImage') || 'სურათი'}</th>
                                    <th>{t('admin.blogList.colTitle') || 'სათაური'}</th>
                                    <th>{t('admin.blogList.colLang') || 'ენა'}</th>
                                    <th>{t('admin.blogList.colAuthor') || 'ავტორი'}</th>
                                    <th>{t('admin.blogList.colDate') || 'თარიღი'}</th>
                                    <th>{t('admin.blogList.colActions') || 'მოქმედებები'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredBlogs.map(blog => (
                                    <tr key={blog.id}>
                                        <td>
                                            <img
                                                src={formatImageUrl(blog.image, '/placeholder.jpg')}
                                                alt=""
                                                style={{ width: '48px', height: '36px', objectFit: 'cover', borderRadius: '6px' }}
                                            />
                                        </td>
                                        <td className="fw-bold text-dark">{blog.title}</td>
                                        <td><LangBadge lang={blog.lang} /></td>
                                        <td className="small text-muted">{blog.author}</td>
                                        <td className="small text-muted">{blog.date}</td>
                                        <td>
                                            <div className="d-flex gap-2">
                                                <button className="shadcn-btn shadcn-btn-sm shadcn-btn-secondary" onClick={() => navigate(`/admin/blogs/edit/${blog.id}`)}>
                                                    <Edit3 size={14} /> {t('common.edit') || 'რედაქტირება'}
                                                </button>
                                                <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteBlog(blog.id)}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {filteredBlogs.length === 0 && <p className="text-center text-muted py-5 mb-0">{t('admin.blogList.noPosts') || 'სტატიები არ მოიძებნა.'}</p>}
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminBlogList;
